import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Users } from "lucide-react";

const EMPTY = { name:"", description:"", full_description:"", banner_url:"", category:"Career",
  event_type:"Webinar", mode:"Online", location:"", meeting_link:"", event_start:"", event_end:"",
  timezone:"Asia/Kolkata", registration_deadline:"", eligibility:"", capacity:100, skills:[],
  organizer:"", contact_email:"", status:"Published", featured:false };

export default function AdminCareerEvents() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [regs, setRegs] = useState(null);

  const load = () => api.get("/admin/career-events").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setF(EMPTY); setOpen(true); };
  const startEdit = (e) => { setEditing(e); setF({ ...EMPTY, ...e, skills: e.skills || [] }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...f, capacity: parseInt(f.capacity || 0, 10),
        skills: Array.isArray(f.skills) ? f.skills : String(f.skills || "").split(",").map(s=>s.trim()).filter(Boolean) };
      if (editing) await api.put(`/admin/career-events/${editing.id}`, payload);
      else await api.post("/admin/career-events", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Archive this event?")) return;
    await api.delete(`/admin/career-events/${id}`); toast.success("Archived"); load();
  };
  const viewRegs = async (e) => {
    const { data } = await api.get(`/admin/career-events/${e.id}/registrations`);
    setRegs({ event: e, registrations: data });
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Career Events</h1>
          <p className="text-slate-400 text-sm mt-1">Webinars, workshops, campus drives and meetups.</p>
        </div>
        <Button onClick={startNew} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="new-event-btn"><Plus className="w-4 h-4 mr-2"/>New Event</Button>
      </div>

      <div className="grid gap-3">
        {rows.length === 0 ? <p className="text-slate-400 text-sm">No events yet. Create your first career event.</p> :
        rows.map(e => (
          <Card key={e.id} className="bg-slate-900 border-slate-800 p-4 flex items-center justify-between gap-4" data-testid={`event-${e.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold">{e.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">{e.event_type} · {e.mode} · {e.event_start ? new Date(e.event_start).toLocaleString() : "TBA"}</div>
              <div className="flex gap-2 mt-2">
                <Badge className="bg-slate-800 border-slate-700 text-slate-200">{e.status}</Badge>
                {e.featured && <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/40">Featured</Badge>}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={()=>viewRegs(e)}><Users className="w-3 h-3 mr-1"/>Registrations</Button>
              <Button size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={()=>startEdit(e)}><Edit2 className="w-3 h-3"/></Button>
              <Button size="sm" variant="outline" className="border-red-800 text-red-300" onClick={()=>remove(e.id)}>Archive</Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-800 text-white max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit event" : "New career event"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={f.name} onChange={e=>setF({...f, name:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
            <div><Label>Short description</Label><Textarea rows={2} value={f.description} onChange={e=>setF({...f, description:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
            <div><Label>Full description</Label><Textarea rows={4} value={f.full_description} onChange={e=>setF({...f, full_description:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={f.event_type} onValueChange={v=>setF({...f, event_type:v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue/></SelectTrigger>
                  <SelectContent>{["Webinar","Workshop","Meetup","Conference","Info Session","Campus Drive"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Mode</Label>
                <Select value={f.mode} onValueChange={v=>setF({...f, mode:v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue/></SelectTrigger>
                  <SelectContent>{["Online","In-Person","Hybrid"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input type="datetime-local" value={f.event_start?.slice(0,16)||""} onChange={e=>setF({...f, event_start: e.target.value})} className="bg-slate-800 border-slate-700"/></div>
              <div><Label>End</Label><Input type="datetime-local" value={f.event_end?.slice(0,16)||""} onChange={e=>setF({...f, event_end: e.target.value})} className="bg-slate-800 border-slate-700"/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Location</Label><Input value={f.location} onChange={e=>setF({...f, location:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
              <div><Label>Meeting link (https)</Label><Input value={f.meeting_link} onChange={e=>setF({...f, meeting_link:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Capacity</Label><Input type="number" value={f.capacity} onChange={e=>setF({...f, capacity:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
              <div><Label>Deadline</Label><Input type="datetime-local" value={f.registration_deadline?.slice(0,16)||""} onChange={e=>setF({...f, registration_deadline:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
              <div><Label>Status</Label>
                <Select value={f.status} onValueChange={v=>setF({...f, status:v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue/></SelectTrigger>
                  <SelectContent>{["Draft","Published","Registration Open","Registration Closed","Completed","Cancelled","Archived"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Skills / Topics (comma-separated)</Label><Input value={Array.isArray(f.skills) ? f.skills.join(", ") : f.skills} onChange={e=>setF({...f, skills:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
            <div><Label>Banner URL (https)</Label><Input value={f.banner_url} onChange={e=>setF({...f, banner_url:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
            <div className="flex items-center justify-between"><Label>Featured</Label><Switch checked={!!f.featured} onCheckedChange={v=>setF({...f, featured:!!v})}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="border-slate-700 text-slate-200">Cancel</Button>
            <Button onClick={save} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="event-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!regs} onOpenChange={(v)=>!v && setRegs(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-800 text-white">
          {regs && <>
            <DialogHeader><DialogTitle>{regs.event.name} · {regs.registrations.length} registrations</DialogTitle></DialogHeader>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {regs.registrations.length === 0 ? <p className="text-slate-400 text-sm">No registrations yet.</p> :
              regs.registrations.map(r => (
                <div key={r.id} className="border border-slate-800 rounded p-3 flex justify-between">
                  <div><div className="text-white font-medium">{r.candidate_name}</div><div className="text-xs text-slate-400">{r.candidate_email}</div></div>
                  <Badge className="bg-slate-800 border-slate-700 text-slate-200">{r.status}</Badge>
                </div>
              ))}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
